// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import DayJS from 'react-dayjs'
import PropTypes from 'prop-types'
import {
  Anchor,
  Box,
  Button,
  NameValueList,
  NameValuePair,
  ResponsiveContext
} from 'grommet'
import { useTranslation } from 'react-i18next'
import { ThemeContext } from 'styled-components'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Loader, Typography } from '../../../../components'
import { GROUP_SCHEMAS } from '../constants'

const GroupDetailDetailsSection = ({
  groupDetailsData,
  parentLoading = false
}) => {
  const { t } = useTranslation(['common', 'iam'])
  const size = useContext(ResponsiveContext)
  const LDFlags = useFlags()
  const navigate = useNavigate()
  const showScimServices = LDFlags['glcp-scim-idp-integration']
  const {
    nameValuePair: { name }
  } = useContext(ThemeContext)

  const getSourceDetail = () => {
    if (groupDetailsData?.source?.toUpperCase() === 'LOCAL') {
      return (
        <Typography type="text" testId="group-user-source">
          {groupDetailsData?.source}
        </Typography>
      )
    }
    return (
      <Anchor
        label={
          <Button
            plain
            label={groupDetailsData?.source}
            testId="group-user-source-btn"
            onClick={() => {
              return navigate(
                `/manage-account/organization/external-scim/${groupDetailsData?.sourceInstance}`
              )
            }}
          />
        }
      />
    )
  }

  return (
    <Box width="xxlarge" pad={{ top: 'medium' }}>
      <Typography type="heading" level="2">
        {t('common:details')}
      </Typography>
      <Box pad={{ top: 'medium' }} direction="column" justify="start">
        <Box width="large" align="start" fill gap="medium">
          {parentLoading ? (
            <Loader testId="group-details-name-value-pair-loader" />
          ) : (
            <NameValueList
              nameProps={{ width: 'small' }}
              pairProps={{ direction: size === 'xsmall' ? 'column' : 'row' }}
            >
              <NameValuePair
                name={
                  <Typography
                    testId="group-details-name-text"
                    type="text"
                    {...name}
                  >
                    {t('common:name')}
                  </Typography>
                }
              >
                <Typography testId="group-details-name" type="text">
                  {groupDetailsData?.displayName || '--'}
                </Typography>
              </NameValuePair>
              <NameValuePair
                name={
                  <Typography
                    testId="group-details-description-text"
                    type="text"
                    {...name}
                  >
                    {t('common:description')}
                  </Typography>
                }
              >
                <Typography testId="group-details-description" type="text">
                  {groupDetailsData[
                    'urn:ietf:params:scim:schemas:extensions:hpe-greenlake:2.0:Group'
                  ]?.groupDescription
                    ? groupDetailsData[
                        'urn:ietf:params:scim:schemas:extensions:hpe-greenlake:2.0:Group'
                      ]?.groupDescription
                    : groupDetailsData?.description || '--'}
                </Typography>
              </NameValuePair>

              {showScimServices && (
                <NameValuePair
                  name={
                    <Typography
                      testId="group-details-source-text"
                      type="text"
                      {...name}
                    >
                      {t('iam:igc_users.source')}
                    </Typography>
                  }
                >
                  <Typography testId="group-details-source" type="text">
                    {getSourceDetail()}
                  </Typography>
                </NameValuePair>
              )}
              {!showScimServices && (
                <NameValuePair
                  name={
                    <Typography
                      testId="group-details-source-text"
                      type="text"
                      {...name}
                    >
                      {t('common:source')}
                    </Typography>
                  }
                >
                  <Typography testId="group-details-source" type="text">
                    {groupDetailsData?.[GROUP_SCHEMAS?.PARTIAL]?.source || '--'}
                  </Typography>
                </NameValuePair>
              )}
              <NameValuePair
                name={
                  <Typography
                    testId="group-details-created-date-text"
                    type="text"
                    {...name}
                  >
                    {t('common:date_created')}
                  </Typography>
                }
              >
                <Typography testId="group-details-created-date" type="text">
                  {groupDetailsData?.meta?.created ? (
                    <DayJS format="LL">{groupDetailsData?.meta?.created}</DayJS>
                  ) : (
                    <DayJS format="LL">{groupDetailsData?.created} </DayJS> ||
                    '--'
                  )}
                </Typography>
              </NameValuePair>

              <NameValuePair
                name={
                  <Typography
                    testId="group-details-last-updated-text"
                    type="text"
                    {...name}
                  >
                    {t('common:last_updated')}
                  </Typography>
                }
              >
                <Typography testId="group-details-last-updated" type="text">
                  {groupDetailsData?.meta?.lastModified ? (
                    <DayJS format="LL">
                      {groupDetailsData?.meta?.lastModified}
                    </DayJS>
                  ) : (
                    (
                      <DayJS format="LL">
                        {groupDetailsData?.lastModified}
                      </DayJS>
                    ) || '--'
                  )}
                </Typography>
              </NameValuePair>
            </NameValueList>
          )}
        </Box>
      </Box>
    </Box>
  )
}

GroupDetailDetailsSection.propTypes = {
  groupDetailsData: PropTypes.object.isRequired,
  parentLoading: PropTypes.bool
}

export default GroupDetailDetailsSection
